/*
 * Header search dropdown, served from a local catalogue index.
 *
 * Two problems this solves, both found on 2026-09-27:
 *   1. /search/suggest answers 417 «Unsupported buyer locale» store-wide, so
 *      Dawn's predictive search never rendered anything.
 *   2. Shopify's search index matches word prefixes and needs three
 *      characters: «اوش» finds 6 products, «سق» and «س» find none. Ameen asked
 *      that any letter find every product whose words contain it.
 *
 * So we filter snippets/afaq-search-index.liquid in the browser. Arabic
 * spelling variants are folded together before matching, which the server
 * index does not do either (أ إ آ → ا, ى → ي, ة → ه, diacritics, tatweel).
 *
 * If the index is missing or a term finds nothing locally, we fall through to
 * the server path in assets/predictive-search.js, which still has its own
 * suggest → /search fallback.
 */
(function () {
  const INDEX_ID = 'afaq-search-index';
  const LIMIT = 6;

  let index = null;

  function loadIndex() {
    if (index !== null) return index;
    const el = document.getElementById(INDEX_ID);
    if (!el) return (index = []);
    try {
      index = JSON.parse(el.textContent).map((item) => ({
        ...item,
        n: normalize(`${item.t} ${item.k || ''}`),
        nt: normalize(item.t),
        s: skeleton(`${item.t} ${item.k || ''}`),
      }));
    } catch (e) {
      index = [];
    }
    return index;
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[ً-ْـٰ]/g, '') // harakat + tatweel
      .replace(/[أإآٱ]/g, 'ا') // أ إ آ ٱ → ا
      .replace(/[ى]/g, 'ي') // ى → ي
      .replace(/[ؤ]/g, 'و') // ؤ → و
      .replace(/[ئ]/g, 'ي') // ئ → ي
      .replace(/[ة]/g, 'ه') // ة → ه
      .replace(/[‌‍‎‏]/g, '')
      .replace(/[·|/\\,،.\-_+()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Broken plurals defeat plain substring matching: «حريق» is not inside
  // «الحرائق». Dropping the long vowels leaves حرق in both, so a second pass
  // over this skeleton catches the pair. Looser, so it only runs when the
  // direct match found nothing.
  function skeleton(value) {
    return normalize(value).replace(/[\u0627\u0648\u064A]/g, '');
  }

  function search(term) {
    const items = loadIndex();
    const q = normalize(term);
    if (!q || !items.length) return [];
    const words = q.split(' ').filter(Boolean);
    const scored = [];

    items.forEach((item) => {
      const everyWord = words.every((w) => item.n.includes(w));
      if (!everyWord) return;
      let score = 3;
      if (item.nt.includes(q)) score = 2;
      if (item.nt.startsWith(q) || item.nt.split(' ').some((w) => w.startsWith(q))) score = 1;
      scored.push({ item, score });
    });

    if (!scored.length) {
      const sq = skeleton(term);
      if (sq.length >= 2) {
        items.forEach((item) => {
          if (item.s.includes(sq)) scored.push({ item, score: 4 });
        });
      }
    }

    return scored
      .sort((a, b) => a.score - b.score)
      .slice(0, LIMIT)
      .map((entry) => entry.item);
  }

  function escapeHtml(value) {
    return String(value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );
  }

  function buildMarkup(results, term) {
    const searchFor = (window.afaqSearchStrings && window.afaqSearchStrings.searchFor) || 'ابحث عن';
    const items = results
      .map(
        (item, i) => `
        <li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${escapeHtml(item.u)}" class="predictive-search__item${
            item.i ? ' predictive-search__item--link-with-thumbnail' : ''
          } link link--text" tabindex="-1">
            ${
              item.i
                ? `<img class="predictive-search__image" src="${escapeHtml(item.i)}" alt="" width="50" height="50" loading="lazy">`
                : ''
            }
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${escapeHtml(item.t)}</p>
              <span class="predictive-search__item-price caption">${escapeHtml(item.p || '')}</span>
            </div>
          </a>
        </li>`
      )
      .join('');

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
          <div class="predictive-search__result-group">
            <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group">
              ${items}
            </ul>
          </div>
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
            <span data-predictive-search-search-for-text>${escapeHtml(searchFor)} "${escapeHtml(term)}"</span>
          </button>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value data-total-results="${results.length}">
          ${results.length}
        </span>
      </div>`;
  }

  function patch() {
    const El = window.customElements && customElements.get('predictive-search');
    if (!El || El.prototype.afaqLocalSearch) return false;

    const original = El.prototype.getSearchResults;

    El.prototype.getSearchResults = function (searchTerm) {
      // Dawn caches this.statusElement here, and setLiveRegionResults() blows
      // up without it — which would leave renderSearchResults() short of its
      // final open() call, so results render into a panel that never opens.
      this.setLiveRegionLoadingState();

      const results = search(searchTerm);
      if (!results.length) {
        // Nothing locally — let the server try: it also searches descriptions.
        return original.call(this, searchTerm);
      }

      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      const markup = buildMarkup(results, searchTerm);
      const deferred = this.dispatchSearchUpdateEvent(searchTerm);

      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = markup;
      });
      this.renderSearchResults(markup);
      deferred?.resolve({ totalCount: results.length });
    };

    El.prototype.afaqLocalSearch = true;
    return true;
  }

  if (!patch()) {
    if (window.customElements) {
      customElements.whenDefined('predictive-search').then(patch);
    }
    document.addEventListener('DOMContentLoaded', patch);
  }
})();
